'use client';

import { useEffect, useState } from 'react';
import { ROUTES } from 'constant';
import type { DashboardSectionKey } from 'constant/dashboardSections';
import { useRouter } from 'next/navigation';

import { hasSystemModulesFailed } from 'utils/helpers/systemModules';

import { useDashboardSectionAccess } from './useDashboardSectionAccess';

/**
 * Longest a gated page will hold its render waiting for sys-modules. RTK Query derives
 * `isLoading` as `!hasData && isFetching`, so an endpoint that never returns usable data
 * keeps it true for the whole of every refetch — without this bound, a hanging `sys-modules`
 * would sit behind a full-screen loader until the 60s axios timeout.
 */
const SECTION_CHECK_TIMEOUT_MS = 2500;

export interface DashboardSectionGuardResult {
  /** True only while a first, still-plausible sys-modules answer is pending; render a loader. */
  isChecking: boolean;
  /** False once a redirect has been dispatched; render nothing. */
  isAllowed: boolean;
}

/**
 * Route-level guard generalizing the redirect-on-mount pattern already used by the
 * Memberships/Gift Cards pages (`if (!isAvailable) router.push(ROUTES.dashboard)`), now driven
 * by the runtime sys-modules flag instead of a static config boolean.
 *
 * Fail-open throughout: a section is only ever hidden by an explicit `false`/`0` from a
 * payload we actually understood. If sys-modules 400s, times out or answers with something
 * unrecognisable, the guard steps aside for the rest of the session and the tenant's static
 * config decides visibility, exactly as it did before sys-modules existed.
 */
export const useDashboardSectionGuard = (
  key: DashboardSectionKey,
  fallbackRoute: string = ROUTES.dashboard
): DashboardSectionGuardResult => {
  const router = useRouter();
  const { isSectionEnabled, isLoading, isFallback, refetch } =
    useDashboardSectionAccess();
  const [hasWaitLapsed, setHasWaitLapsed] = useState(false);
  const isAllowed = isSectionEnabled(key);

  useEffect(() => {
    // Freshens the shared sys-modules cache on every navigation into a gated page — but
    // never re-arms an endpoint already known to be broken; that session stays on the
    // static config rather than paying for (and blocking on) a request that will fail again.
    if (hasSystemModulesFailed()) {
      return;
    }

    refetch();
    // Intentionally mount-only: one freshen per navigation, not one per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(
      () => setHasWaitLapsed(true),
      SECTION_CHECK_TIMEOUT_MS
    );

    return () => clearTimeout(timer);
  }, []);

  const isChecking = isLoading && !isFallback && !hasWaitLapsed;

  useEffect(() => {
    if (!isChecking && !isAllowed) {
      router.push(fallbackRoute);
    }
  }, [isChecking, isAllowed, router, fallbackRoute]);

  return { isChecking, isAllowed };
};
